import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "../lib/api";
import PageHead from "../components/PageHead";
import { toast } from "sonner";
import {
  TelegramLogo, WhatsappLogo, RedditLogo, XLogo, CheckCircle, UploadSimple,
  ShieldCheck, Lightning, BellRinging, Brain, QrCode, Power, ArrowClockwise,
} from "@phosphor-icons/react";

function Card({ icon: Icon, tint, title, badge, children }) {
  return (
    <div className="glass-strong rounded-3xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
        <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${tint}`}><Icon size={18} weight="bold" /></div>
        <span className="font-display font-bold">{title}</span>
        {badge}
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button data-testid={testid} onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full p-0.5 transition-colors duration-200 ${checked ? "brand-gradient" : "bg-foreground/15"}`}>
      <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

const Input = (p) => <input {...p} className={`w-full rounded-2xl bg-background/60 border border-border px-4 py-2.5 text-sm outline-none focus:border-brand transition-colors duration-200 ${p.className || ""}`} />;
const Connected = () => <span className="ml-auto flex items-center gap-1 text-xs text-bull font-medium"><CheckCircle size={13} weight="fill" /> Connected</span>;

export default function Integrations() {
  const [cfg, setCfg] = useState(null);
  const [tgToken, setTgToken] = useState("");
  const [alertChat, setAlertChat] = useState("");
  const [threshold, setThreshold] = useState(60);
  const [interval, setIntervalVal] = useState(60);
  const [redditId, setRedditId] = useState("");
  const [redditSecret, setRedditSecret] = useState("");
  const [subs, setSubs] = useState("");
  const [twKey, setTwKey] = useState("");
  const [waStatus, setWaStatus] = useState(null);
  const [llmKey, setLlmKey] = useState("");
  const fileRef = useRef();
  const waPoll = useRef();

  const load = () => api.get("/config").then((r) => {
    const c = r.data; setCfg(c);
    setAlertChat(c.alert_chat_id || ""); setThreshold(c.alert_threshold ?? 60);
    setIntervalVal(c.scheduler_interval ?? 60); setSubs((c.reddit_subreddits || []).join(", "));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (fields, msg) => {
    try { await api.put("/config", fields); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const testTg = async () => {
    try { const { data } = await api.post("/telegram/test", { telegram_bot_token: tgToken || null }); toast.success(`Connected to @${data.bot.username}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const testAlert = async () => {
    try { await api.post("/alerts/test"); toast.success("Test alert sent to Telegram"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const fetchReddit = async () => {
    try { const { data } = await api.post("/reddit/fetch"); toast.success(`Fetched — ${data.inserted} new posts`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const uploadWA = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/messages/whatsapp?group_name=WhatsApp Import`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Parsed ${data.parsed} messages`); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const pollWA = () => {
    api.get("/whatsapp/status").then((r) => setWaStatus(r.data)).catch(() => {});
  };
  const connectWA = async () => {
    try { await api.post("/whatsapp/connect"); toast.info("Bridge starting — QR will appear shortly"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    clearInterval(waPoll.current); waPoll.current = setInterval(pollWA, 3000); pollWA();
  };
  const disconnectWA = async () => { await api.post("/whatsapp/disconnect"); clearInterval(waPoll.current); pollWA(); };
  useEffect(() => () => clearInterval(waPoll.current), []);

  if (!cfg) return <div className="text-muted-foreground py-10">Loading…</div>;

  return (
    <>
      <PageHead title="Integrations" subtitle="Connect your desks, data & automation" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Auto-refresh */}
        <Card icon={Lightning} tint="bg-brand/10 text-brand" title="Auto-Refresh">
          <div className="flex items-center justify-between">
            <div><div className="text-sm font-medium">Scheduler</div><div className="text-xs text-muted-foreground">Auto-run ingest → analysis → outcomes</div></div>
            <Toggle testid="scheduler-toggle" checked={cfg.scheduler_enabled} onChange={(v) => patch({ scheduler_enabled: v }, v ? "Scheduler on" : "Scheduler off")} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground flex-1">Interval (seconds)</span>
            <Input data-testid="scheduler-interval" type="number" min="15" value={interval} onChange={(e) => setIntervalVal(Number(e.target.value))} className="w-24" />
            <button data-testid="scheduler-save" onClick={() => patch({ scheduler_interval: interval }, "Interval saved")} className="brand-gradient text-white rounded-2xl px-3 py-2 text-sm font-medium">Save</button>
          </div>
          <p className="text-xs text-muted-foreground">Minimum 15s to protect API rate limits. Market data is cached ~2.5 min.</p>
        </Card>

        {/* Alerts */}
        <Card icon={BellRinging} tint="bg-neutral/10 text-neutral" title="Conviction Alerts">
          <p className="text-xs text-muted-foreground">Push high-conviction signals to a Telegram chat/channel (uses your bot token below).</p>
          <Input data-testid="alert-chat" value={alertChat} onChange={(e) => setAlertChat(e.target.value)} placeholder="Alert chat ID (e.g. -1001234567890)" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground flex-1">Threshold {threshold}</span>
            <input data-testid="alert-threshold" type="range" min="0" max="100" step="5" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="accent-brand flex-1" />
          </div>
          <div className="flex gap-2">
            <button data-testid="alert-save" onClick={() => patch({ alert_chat_id: alertChat, alert_threshold: threshold }, "Alerts saved")} className="flex-1 brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold">Save</button>
            <button data-testid="alert-test" onClick={testAlert} className="flex-1 glass rounded-2xl py-2.5 text-sm font-medium hover:bg-foreground/5 transition-colors duration-200">Send Test</button>
          </div>
        </Card>

        {/* Telegram */}
        <Card icon={TelegramLogo} tint="bg-sky-500/10 text-sky-500" title="Telegram Bot" badge={cfg.telegram_configured && <Connected />}>
          <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Create a bot via <span className="font-mono text-foreground">@BotFather</span></li>
            <li>Add it to groups & disable privacy mode so it reads all messages</li>
          </ol>
          {cfg.telegram_configured && <div className="text-xs font-mono text-muted-foreground">Current: {cfg.telegram_token_masked}</div>}
          <Input data-testid="telegram-token" value={tgToken} onChange={(e) => setTgToken(e.target.value)} placeholder="123456:ABC-DEF…" />
          <div className="flex gap-2">
            <button data-testid="telegram-test" onClick={testTg} className="flex-1 glass rounded-2xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-foreground/5 transition-colors duration-200"><ShieldCheck size={15} weight="bold" /> Test</button>
            <button data-testid="telegram-save" onClick={() => patch({ telegram_bot_token: tgToken }, "Telegram connected")} disabled={!tgToken.trim()} className="flex-1 brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold disabled:opacity-60">Save & Connect</button>
          </div>
          {cfg.discovered_chats?.length > 0 && (
            <div className="pt-1 space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Discovered chats</div>
              {cfg.discovered_chats.map((c) => <div key={c.id} className="flex justify-between text-xs font-mono glass rounded-xl px-3 py-1.5"><span>{c.name}</span><span className="text-muted-foreground">{c.id}</span></div>)}
            </div>
          )}
        </Card>

        {/* WhatsApp */}
        <Card icon={WhatsappLogo} tint="bg-bull/10 text-bull" title="WhatsApp Web">
          <p className="text-xs text-muted-foreground">Unofficial Web bridge (links your number like WhatsApp Web). <span className="text-neutral">May risk a ban — use a secondary number.</span></p>
          <div className="flex gap-2">
            <button data-testid="wa-connect" onClick={connectWA} className="flex-1 brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2"><Power size={15} weight="bold" /> Connect</button>
            <button data-testid="wa-refresh-status" onClick={pollWA} className="glass rounded-2xl px-4 hover:bg-foreground/5 transition-colors duration-200"><ArrowClockwise size={15} weight="bold" /></button>
            <button data-testid="wa-disconnect" onClick={disconnectWA} className="glass rounded-2xl px-4 text-bear hover:bg-bear/10 transition-colors duration-200"><Power size={15} weight="bold" /></button>
          </div>
          {waStatus && (
            <div className="text-center">
              <div className="text-xs text-muted-foreground mb-2">Status: <span className="font-mono text-foreground">{waStatus.state}</span></div>
              {waStatus.qr && <img src={waStatus.qr} alt="WhatsApp QR" data-testid="wa-qr" className="w-44 h-44 mx-auto rounded-2xl bg-white p-2" />}
              {waStatus.note && <div className="text-xs text-muted-foreground mt-2">{waStatus.note}</div>}
            </div>
          )}
          <div className="border-t border-border pt-3">
            <div className="text-xs text-muted-foreground mb-2">Or upload a chat export (.txt)</div>
            <input ref={fileRef} data-testid="wa-file" type="file" accept=".txt" onChange={uploadWA} className="hidden" />
            <button data-testid="wa-upload-btn" onClick={() => fileRef.current?.click()} className="w-full glass rounded-2xl border border-dashed border-border py-4 text-xs text-muted-foreground hover:text-foreground hover:border-brand/50 flex items-center justify-center gap-2 transition-colors duration-200"><UploadSimple size={16} weight="bold" /> Upload export</button>
          </div>
        </Card>

        {/* Reddit */}
        <Card icon={RedditLogo} tint="bg-orange-500/10 text-orange-500" title="Reddit" badge={cfg.reddit_configured && <Connected />}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Enable auto-fetch</div>
            <Toggle testid="reddit-toggle" checked={cfg.reddit_enabled} onChange={(v) => patch({ reddit_enabled: v }, v ? "Reddit on" : "Reddit off")} />
          </div>
          <p className="text-xs text-muted-foreground">Anonymous Reddit is blocked from cloud IPs — add a free "script" app's credentials (<span className="font-mono">reddit.com/prefs/apps</span>).</p>
          <Input data-testid="reddit-id" value={redditId} onChange={(e) => setRedditId(e.target.value)} placeholder="Client ID" />
          <Input data-testid="reddit-secret" value={redditSecret} onChange={(e) => setRedditSecret(e.target.value)} placeholder="Client secret" />
          <Input data-testid="reddit-subs" value={subs} onChange={(e) => setSubs(e.target.value)} placeholder="subreddits, comma separated" />
          <div className="flex gap-2">
            <button data-testid="reddit-save" onClick={() => patch({ reddit_client_id: redditId, reddit_client_secret: redditSecret, reddit_subreddits: subs.split(",") }, "Reddit saved")} className="flex-1 brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold">Save</button>
            <button data-testid="reddit-fetch" onClick={fetchReddit} className="flex-1 glass rounded-2xl py-2.5 text-sm font-medium hover:bg-foreground/5 transition-colors duration-200">Fetch Now</button>
          </div>
        </Card>

        {/* Twitter */}
        <Card icon={XLogo} tint="bg-foreground/10 text-foreground" title="Twitter / X" badge={cfg.twitter_configured && <Connected />}>
          <p className="text-xs text-muted-foreground">X requires a paid API tier for reading posts. Paste a Bearer token to enable the slot.</p>
          <Input data-testid="twitter-key" value={twKey} onChange={(e) => setTwKey(e.target.value)} placeholder="X API Bearer token" />
          <button data-testid="twitter-save" onClick={() => patch({ twitter_api_key: twKey }, "X token saved")} className="w-full brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold">Save</button>
          <p className="text-[11px] text-muted-foreground">investing.com has no public API and scraping violates its ToS — not wired.</p>
        </Card>

        {/* Sentiment */}
        <Card icon={Brain} tint="bg-brand/10 text-brand" title="Sentiment Engine">
          <div className="flex gap-2">
            {["keyword", "ai"].map((m) => (
              <button key={m} data-testid={`sentiment-${m}`} onClick={() => patch({ sentiment_mode: m }, `Sentiment: ${m}`)}
                className={`flex-1 rounded-2xl py-2.5 text-sm font-medium capitalize transition-all duration-200 ${cfg.sentiment_mode === m ? "brand-gradient text-white" : "glass text-muted-foreground hover:text-foreground"}`}>{m === "ai" ? "AI-powered" : "Keyword"}</button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">AI mode uses your LLM key. Add a provider key (falls back to the built-in Emergent key).</p>
          <select data-testid="sentiment-provider" value={cfg.sentiment_provider} onChange={(e) => patch({ sentiment_provider: e.target.value })} className="w-full rounded-2xl bg-background/60 border border-border px-4 py-2.5 text-sm outline-none">
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI (GPT)</option>
            <option value="gemini">Google (Gemini)</option>
          </select>
          <Input data-testid="sentiment-key" value={llmKey} onChange={(e) => setLlmKey(e.target.value)} placeholder={`${cfg.sentiment_provider} API key`} />
          <button data-testid="sentiment-key-save" onClick={() => { patch({ llm_api_keys: { [cfg.sentiment_provider]: llmKey } }, "LLM key saved"); setLlmKey(""); }} className="w-full brand-gradient text-white rounded-2xl py-2.5 text-sm font-semibold">Save Key</button>
        </Card>

        {/* Market data info */}
        <Card icon={CheckCircle} tint="bg-bull/10 text-bull" title="Market Data">
          <p className="text-xs text-muted-foreground"><span className="text-bull font-semibold">● Yahoo Finance</span> active — NSE/BSE, US (NASDAQ/NYSE), indices (^NSEI, ^GSPC), commodities (GC=F, CL=F), forex (EURUSD=X) & crypto.</p>
          <p className="text-xs text-muted-foreground">Extraction: <span className="font-mono text-foreground">{cfg.extraction_model}</span> · Engine: <span className="text-foreground">Bratz Mega v4.0</span>.</p>
        </Card>
      </div>
    </>
  );
}
